import React, { useState } from 'react';
import { Button, Dialog } from '@teachstonellc/component-library';
import { Errors } from './Styled';

export interface TrainingActionModalProps {
  /** Controls whether the dialog is open. */
  open: boolean;

  /** The title of the dialog. */
  title: string;

  /** The body content of the dialog. */
  content: React.ReactNode;

  /** Label for the cancel button. */
  cancelLabel: string;

  /** Label for the button that performs the action. */
  actionLabel: string;

  /** Performs the action. Rejections are shown as errors in the dialog. */
  action: () => Promise<unknown>;

  /** The field in the API error response that holds the error message. */
  errorField: string;

  /** Message shown when the API error response has no errors. */
  unknownErrorMessage: string;

  /** Callback fired when the component requests to be closed. */
  onClose: () => void;

  /** Callback fired after the action has succeeded. */
  afterSubmit: () => void;
}

const TrainingActionModal: React.FC<TrainingActionModalProps> = ({
  open,
  title,
  content,
  cancelLabel,
  actionLabel,
  action,
  errorField,
  unknownErrorMessage,
  onClose,
  afterSubmit
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState('');
  const hasErrors = Boolean(errors);

  function onModalClose() {
    setErrors('');
    onClose();
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen && !submitting) {
      onModalClose();
    }
  }

  function handleAction() {
    setSubmitting(true);
    action()
      .then(() => {
        setSubmitting(false);
        onModalClose();
        afterSubmit();
      })
      .catch(error => {
        setSubmitting(false);
        const errorMessage = error?.response?.data?.errors?.[errorField]?.[0];
        setErrors(errorMessage || unknownErrorMessage);
      });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      closeButton={
        <Button type="button" variant="secondary" disabled={submitting}>
          {cancelLabel}
        </Button>
      }
      actionButton={
        <Button
          type="button"
          variant="primary"
          onClick={handleAction}
          loading={submitting}
          disabled={hasErrors}
        >
          {actionLabel}
        </Button>
      }
    >
      {content}
      <Errors role="alert">{errors}</Errors>
    </Dialog>
  );
};

export default TrainingActionModal;
